/**
 * The activity calendar panel type. One small square per day, placed on a calendar: a column per
 * week, a row per weekday, shaded by that day's number.
 */

const activity = {
  names: [
    {
      id: 'activity',
      label: 'Activity',
      phrase: 'an activity calendar',
      guide: 'activity.md',
      advanced: true,
      /* A year of weeks is wide and only seven squares tall, so a fresh panel is given the whole
         width and a short height. A creation layout rather than a pinned one: the shape is a
         starting point, and a reader wanting a quarter on screen may make it narrower. */
      creationLayout: { w: 12, h: 4 },
      toolSummary: "whether a daily number held steady or came in bursts: one small square per day on a calendar, shaded by that day's value, the most recent 53 weeks",
      promptSummary: 'whether a daily number held steady across the year or came in bursts: one small square per day laid out as a calendar, a column per week and a row per weekday, each square shaded from the smallest day to the largest, over the most recent 53 weeks'
    }
  ],
  block: 'activity',
  /* Activity calendar. The query returns one row per day; this block only says which column carries
     the day and which carries its number. There is no field for the two axes: a square is placed by
     the date it falls on, so the week and the weekday are read from the day itself rather than
     configured. */
  fields: {
    dateKey: { kind: 'string', required: true, hint: 'a square is one day, placed on the calendar by its date', label: 'Day' },
    valueKey: { kind: 'string', required: true, label: 'Day value' }
  },
  editorHint: 'One row is one day. Aggregate to a single value per day in SQL and return the day as a date or an ISO string; the panel places every square itself, and draws the most recent 53 weeks.',
  stateSummary: ({ block }) => (block.dateKey && block.valueKey ? `activity: ${block.dateKey} × ${block.valueKey}` : ''),
  toolSchema: {
    type: ['object', 'null'],
    additionalProperties: false,
    required: ['dateKey', 'valueKey'],
    description: 'Activity calendar mapping (displayType "activity"): ONE ROW IS ONE DAY. THE ROW ORDER MEANS NOTHING HERE, unlike every other grid type: a square is placed by its date, in a column per week and a row per weekday, so the query owns which days exist rather than where they sit. Aggregate to one value per day in SQL, with no gap-filling: a day the query never returns is drawn as an empty square, which is what "nothing happened" looks like. Capped at the most recent 53 weeks; the renderer enforces that and reports every day it could not draw. Read immutable/analytics/panel-types/activity.md before configuring one.',
    properties: {
      dateKey: { type: 'string', description: 'Column holding the day the square sits on. Dot paths work. A date, or a string a date can be read from ("2026-03-14", "2026-03-14T09:00:00Z"); a bare epoch number is ambiguous between seconds and milliseconds and is counted as undrawable rather than guessed. A value carrying a time is read as the day it names, not converted to another timezone.' },
      valueKey: { type: 'string', description: "Column holding that day's number. Dot paths work. Zero and negative numbers are real data and are drawn; an empty or non-numeric cell is counted as not shown and left blank. A second row landing on a day that already has a square keeps the first and is counted too, so aggregate in SQL." }
    }
  }
};

export default activity;
